import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase/firebaseConfig';

/**
 * Purchase Service
 * 
 * Handles all API calls related to purchases, token balance, and purchase history.
 * This service layer separates backend API calls from frontend components.
 */

/**
 * Get the user's current token balance
 * @returns {Promise<number>} The user's token balance
 */
export const getTokenBalance = async () => {
  try {
    const functions = getFunctions(app);
    const getTokenBalanceFunction = httpsCallable(functions, 'getTokenBalance');
    const result = await getTokenBalanceFunction();

    if (result.data && typeof result.data.tokens === 'number') {
      return result.data.tokens;
    }

    return 0;
  } catch (error) {
    console.error('Error fetching token balance:', error);
    throw error;
  }
};

/**
 * Get purchase history with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Filter by status: 'all', 'active', 'deleted', 'restored'
 * @param {string} filters.contentType - Filter by type: 'all', 'course', 'cookbook'
 * @returns {Promise<Array>} Array of purchase objects
 */
export const getPurchaseHistory = async (filters = {}) => {
  try {
    const functions = getFunctions(app);
    const getPurchaseHistoryFunction = httpsCallable(functions, 'getPurchaseHistory');

    const params = {};
    if (filters.status && filters.status !== 'all') {
      params.status = filters.status;
    }
    if (filters.contentType && filters.contentType !== 'all') {
      params.contentType = filters.contentType;
    }

    const result = await getPurchaseHistoryFunction(params);

    if (result.data && result.data.purchases) {
      return result.data.purchases;
    }

    return [];
  } catch (error) {
    console.error('Error loading purchase history:', error);

    // Handle Firestore index errors gracefully
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      console.log('Firestore index may be required. Returning empty array.');
      return [];
    }

    throw error;
  }
};

/**
 * Restore a deleted purchase
 * @param {string} purchaseId - The ID of the purchase to restore
 * @returns {Promise<Object>} Result object with success status and message
 */
export const restorePurchase = async (purchaseId) => {
  try {
    const functions = getFunctions(app);
    const restorePurchaseFunction = httpsCallable(functions, 'restorePurchase');

    const result = await restorePurchaseFunction({ purchaseId });

    if (result.data && result.data.success) {
      return {
        success: true,
        message: result.data.message || 'Purchase restored successfully!'
      };
    }

    throw new Error(result.data?.message || 'Failed to restore purchase');
  } catch (error) {
    console.error('Error restoring purchase:', error);
    throw error;
  }
};

/**
 * Purchase content from the catalog
 * @param {string} catalogId - The ID of the catalog entry to purchase
 * @returns {Promise<Object>} Result object with success status, contentId, contentType, and tokenBalance
 */
export const purchaseCatalogContent = async (catalogId) => {
  try {
    const functions = getFunctions(app);
    const purchaseCatalogContentFunction = httpsCallable(functions, 'purchaseCatalogContent');

    const result = await purchaseCatalogContentFunction({ catalogId });

    if (result.data && result.data.success) {
      return {
        success: true,
        contentId: result.data.contentId,
        contentType: result.data.contentType,
        tokenBalance: result.data.tokenBalance,
        message: result.data.message || 'Content purchased successfully!'
      };
    }

    throw new Error(result.data?.message || 'Failed to purchase content');
  } catch (error) {
    console.error('Error purchasing catalog content:', error);
    throw error;
  }
};

